import { AppwriteException, Client, type Payload } from '../client';
import type { Models } from '../models';

import { PasswordHash } from '../enums/password-hash';
import { AuthenticatorType } from '../enums/authenticator-type';
import { MessagingProviderType } from '../enums/messaging-provider-type';
export class Users {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Get a list of all the project's users. You can use the query params to filter your results.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name, email, phone, status, passwordUpdate, registration, emailVerification, phoneVerification, labels, impersonator, accessedAt
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.UserList<Preferences>>}
     */
    list<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params?: {
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.UserList<Preferences>>;
    /**
     * Get a list of all the project's users. You can use the query params to filter your results.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: name, email, phone, status, passwordUpdate, registration, emailVerification, phoneVerification, labels, impersonator, accessedAt
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.UserList<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    list<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.UserList<Preferences>>;
    list<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        paramsOrFirst?:
            { queries?: string[]; search?: string; total?: boolean } | string[],
        ...rest: [string?, boolean?]
    ): Promise<Models.UserList<Preferences>> {
        let params: { queries?: string[]; search?: string; total?: boolean };

        if (
            !paramsOrFirst ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                search: rest[0] as string,
                total: rest[1] as boolean,
            };
        }

        const queries = params.queries;
        const search = params.search;
        const total = params.total;
        const apiPath = '/users';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.phone - Phone number. Format this number with a leading '+' and a country code, e.g., +16175551212.
     * @param {string} params.password - Plain text user password. Must be at least 8 chars.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    create<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email?: string;
        phone?: string;
        password?: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} phone - Phone number. Format this number with a leading '+' and a country code, e.g., +16175551212.
     * @param {string} password - Plain text user password. Must be at least 8 chars.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    create<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        userId: string,
        email?: string,
        phone?: string,
        password?: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    create<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        paramsOrFirst:
            | {
                  userId: string;
                  email?: string;
                  phone?: string;
                  password?: string;
                  name?: string;
              }
            | string,
        ...rest: [string?, string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email?: string;
            phone?: string;
            password?: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email?: string;
                phone?: string;
                password?: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                phone: rest[1] as string,
                password: rest[2] as string,
                name: rest[3] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const phone = params.phone;
        const password = params.password;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof phone !== 'undefined') {
            apiPayload['phone'] = phone;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [Argon2](https://en.wikipedia.org/wiki/Argon2) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using Argon2.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createArgon2User<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [Argon2](https://en.wikipedia.org/wiki/Argon2) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using Argon2.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createArgon2User<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createArgon2User<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | { userId: string; email: string; password: string; name?: string }
            | string,
        ...rest: [string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                name: rest[2] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/users/argon2';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [Bcrypt](https://en.wikipedia.org/wiki/Bcrypt) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using Bcrypt.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createBcryptUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [Bcrypt](https://en.wikipedia.org/wiki/Bcrypt) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using Bcrypt.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createBcryptUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createBcryptUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | { userId: string; email: string; password: string; name?: string }
            | string,
        ...rest: [string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                name: rest[2] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/users/bcrypt';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Get identities for all users.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, provider, providerUid, providerEmail, providerAccessTokenExpiry
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.IdentityList>}
     */
    listIdentities(params?: {
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.IdentityList>;
    /**
     * Get identities for all users.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, provider, providerUid, providerEmail, providerAccessTokenExpiry
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.IdentityList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listIdentities(
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.IdentityList>;
    listIdentities(
        paramsOrFirst?:
            { queries?: string[]; search?: string; total?: boolean } | string[],
        ...rest: [string?, boolean?]
    ): Promise<Models.IdentityList> {
        let params: { queries?: string[]; search?: string; total?: boolean };

        if (
            !paramsOrFirst ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                search: rest[0] as string,
                total: rest[1] as boolean,
            };
        }

        const queries = params.queries;
        const search = params.search;
        const total = params.total;
        const apiPath = '/users/identities';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete an identity by its unique ID.
     *
     * @param {string} params.identityId - Identity ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteIdentity(params: { identityId: string }): Promise<{}>;
    /**
     * Delete an identity by its unique ID.
     *
     * @param {string} identityId - Identity ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteIdentity(identityId: string): Promise<{}>;
    deleteIdentity(
        paramsOrFirst: { identityId: string } | string,
    ): Promise<{}> {
        let params: { identityId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { identityId: string };
        } else {
            params = {
                identityId: paramsOrFirst as string,
            };
        }

        const identityId = params.identityId;
        if (typeof identityId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "identityId"',
            );
        }
        const apiPath = '/users/identities/{identityId}'.replace(
            '{identityId}',
            encodeURIComponent(String(identityId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [MD5](https://en.wikipedia.org/wiki/MD5) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using MD5.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createMD5User<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [MD5](https://en.wikipedia.org/wiki/MD5) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using MD5.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMD5User<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createMD5User<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | { userId: string; email: string; password: string; name?: string }
            | string,
        ...rest: [string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                name: rest[2] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/users/md5';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [PHPass](https://www.openwall.com/phpass/) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or pass the string `ID.unique()`to auto generate it. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using PHPass.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createPHPassUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [PHPass](https://www.openwall.com/phpass/) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or pass the string `ID.unique()`to auto generate it. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using PHPass.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPHPassUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createPHPassUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | { userId: string; email: string; password: string; name?: string }
            | string,
        ...rest: [string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                name: rest[2] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/users/phpass';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [Scrypt](https://github.com/Tarsnap/scrypt) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using Scrypt.
     * @param {string} params.passwordSalt - Optional salt used to hash password.
     * @param {number} params.passwordCpu - Optional CPU cost used to hash password.
     * @param {number} params.passwordMemory - Optional memory cost used to hash password.
     * @param {number} params.passwordParallel - Optional parallelization cost used to hash password.
     * @param {number} params.passwordLength - Optional hash length used to hash password.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createScryptUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        passwordSalt: string;
        passwordCpu: number;
        passwordMemory: number;
        passwordParallel: number;
        passwordLength: number;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [Scrypt](https://github.com/Tarsnap/scrypt) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using Scrypt.
     * @param {string} passwordSalt - Optional salt used to hash password.
     * @param {number} passwordCpu - Optional CPU cost used to hash password.
     * @param {number} passwordMemory - Optional memory cost used to hash password.
     * @param {number} passwordParallel - Optional parallelization cost used to hash password.
     * @param {number} passwordLength - Optional hash length used to hash password.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createScryptUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        passwordSalt: string,
        passwordCpu: number,
        passwordMemory: number,
        passwordParallel: number,
        passwordLength: number,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createScryptUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | {
                  userId: string;
                  email: string;
                  password: string;
                  passwordSalt: string;
                  passwordCpu: number;
                  passwordMemory: number;
                  passwordParallel: number;
                  passwordLength: number;
                  name?: string;
              }
            | string,
        ...rest: [
            string?,
            string?,
            string?,
            number?,
            number?,
            number?,
            number?,
            string?,
        ]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            passwordSalt: string;
            passwordCpu: number;
            passwordMemory: number;
            passwordParallel: number;
            passwordLength: number;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                passwordSalt: string;
                passwordCpu: number;
                passwordMemory: number;
                passwordParallel: number;
                passwordLength: number;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                passwordSalt: rest[2] as string,
                passwordCpu: rest[3] as number,
                passwordMemory: rest[4] as number,
                passwordParallel: rest[5] as number,
                passwordLength: rest[6] as number,
                name: rest[7] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const passwordSalt = params.passwordSalt;
        const passwordCpu = params.passwordCpu;
        const passwordMemory = params.passwordMemory;
        const passwordParallel = params.passwordParallel;
        const passwordLength = params.passwordLength;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        if (typeof passwordSalt === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordSalt"',
            );
        }
        if (typeof passwordCpu === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordCpu"',
            );
        }
        if (typeof passwordMemory === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordMemory"',
            );
        }
        if (typeof passwordParallel === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordParallel"',
            );
        }
        if (typeof passwordLength === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordLength"',
            );
        }
        const apiPath = '/users/scrypt';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof passwordSalt !== 'undefined') {
            apiPayload['passwordSalt'] = passwordSalt;
        }
        if (typeof passwordCpu !== 'undefined') {
            apiPayload['passwordCpu'] = passwordCpu;
        }
        if (typeof passwordMemory !== 'undefined') {
            apiPayload['passwordMemory'] = passwordMemory;
        }
        if (typeof passwordParallel !== 'undefined') {
            apiPayload['passwordParallel'] = passwordParallel;
        }
        if (typeof passwordLength !== 'undefined') {
            apiPayload['passwordLength'] = passwordLength;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [Scrypt Modified](https://gist.github.com/Meldiron/eecf84a0225eccb5a378d45bb27462cc) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using Scrypt Modified.
     * @param {string} params.passwordSalt - Salt used to hash password.
     * @param {string} params.passwordSaltSeparator - Salt separator used to hash password.
     * @param {string} params.passwordSignerKey - Signer key used to hash password.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createScryptModifiedUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        passwordSalt: string;
        passwordSaltSeparator: string;
        passwordSignerKey: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [Scrypt Modified](https://gist.github.com/Meldiron/eecf84a0225eccb5a378d45bb27462cc) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using Scrypt Modified.
     * @param {string} passwordSalt - Salt used to hash password.
     * @param {string} passwordSaltSeparator - Salt separator used to hash password.
     * @param {string} passwordSignerKey - Signer key used to hash password.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createScryptModifiedUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        passwordSalt: string,
        passwordSaltSeparator: string,
        passwordSignerKey: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createScryptModifiedUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | {
                  userId: string;
                  email: string;
                  password: string;
                  passwordSalt: string;
                  passwordSaltSeparator: string;
                  passwordSignerKey: string;
                  name?: string;
              }
            | string,
        ...rest: [string?, string?, string?, string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            passwordSalt: string;
            passwordSaltSeparator: string;
            passwordSignerKey: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                passwordSalt: string;
                passwordSaltSeparator: string;
                passwordSignerKey: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                passwordSalt: rest[2] as string,
                passwordSaltSeparator: rest[3] as string,
                passwordSignerKey: rest[4] as string,
                name: rest[5] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const passwordSalt = params.passwordSalt;
        const passwordSaltSeparator = params.passwordSaltSeparator;
        const passwordSignerKey = params.passwordSignerKey;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        if (typeof passwordSalt === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordSalt"',
            );
        }
        if (typeof passwordSaltSeparator === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordSaltSeparator"',
            );
        }
        if (typeof passwordSignerKey === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "passwordSignerKey"',
            );
        }
        const apiPath = '/users/scrypt-modified';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof passwordSalt !== 'undefined') {
            apiPayload['passwordSalt'] = passwordSalt;
        }
        if (typeof passwordSaltSeparator !== 'undefined') {
            apiPayload['passwordSaltSeparator'] = passwordSaltSeparator;
        }
        if (typeof passwordSignerKey !== 'undefined') {
            apiPayload['passwordSignerKey'] = passwordSignerKey;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a new user. Password provided must be hashed with the [SHA](https://en.wikipedia.org/wiki/Secure_Hash_Algorithm) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - User password hashed using SHA.
     * @param {PasswordHash} params.passwordVersion - Optional SHA version used to hash password. Allowed values are: 'sha1', 'sha224', 'sha256', 'sha384', 'sha512/224', 'sha512/256', 'sha512', 'sha3-224', 'sha3-256', 'sha3-384', 'sha3-512'
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    createSHAUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        passwordVersion?: PasswordHash;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Create a new user. Password provided must be hashed with the [SHA](https://en.wikipedia.org/wiki/Secure_Hash_Algorithm) algorithm. Use the [POST /users](https://appwrite.io/docs/server/users#usersCreate) endpoint to create users with a plain text password.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - User password hashed using SHA.
     * @param {PasswordHash} passwordVersion - Optional SHA version used to hash password. Allowed values are: 'sha1', 'sha224', 'sha256', 'sha384', 'sha512/224', 'sha512/256', 'sha512', 'sha3-224', 'sha3-256', 'sha3-384', 'sha3-512'
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSHAUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        email: string,
        password: string,
        passwordVersion?: PasswordHash,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    createSHAUser<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            | {
                  userId: string;
                  email: string;
                  password: string;
                  passwordVersion?: PasswordHash;
                  name?: string;
              }
            | string,
        ...rest: [string?, string?, PasswordHash?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            passwordVersion?: PasswordHash;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                passwordVersion?: PasswordHash;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                passwordVersion: rest[2] as PasswordHash,
                name: rest[3] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const passwordVersion = params.passwordVersion;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/users/sha';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof passwordVersion !== 'undefined') {
            apiPayload['passwordVersion'] = passwordVersion;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a user by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    get<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: { userId: string }): Promise<Models.User<Preferences>>;
    /**
     * Get a user by its unique ID.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    get<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        userId: string,
    ): Promise<Models.User<Preferences>>;
    get<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete a user by its unique ID, thereby releasing it's ID. Since ID is released and can be reused, all user-related resources like documents or storage files should be deleted before user deletion. If you want to keep ID reserved, use the [updateStatus](https://appwrite.io/docs/server/users#usersUpdateStatus) endpoint instead.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(params: { userId: string }): Promise<{}>;
    /**
     * Delete a user by its unique ID, thereby releasing it's ID. Since ID is released and can be reused, all user-related resources like documents or storage files should be deleted before user deletion. If you want to keep ID reserved, use the [updateStatus](https://appwrite.io/docs/server/users#usersUpdateStatus) endpoint instead.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    delete(userId: string): Promise<{}>;
    delete(paramsOrFirst: { userId: string } | string): Promise<{}> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user email by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.email - User email.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateEmail<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user email by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string} email - User email.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEmail<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, email: string): Promise<Models.User<Preferences>>;
    updateEmail<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; email: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; email: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string; email: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        const apiPath = '/users/{userId}/email'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Enable or disable whether a user can impersonate other users. When impersonation headers are used, the request runs as the target user for API behavior, while internal audit logs still attribute the action to the original impersonator and store the impersonated target details only in internal audit payload data.
     *
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.impersonator - Whether the user can impersonate other users. When true, the user can browse project users to choose a target and can pass impersonation headers to act as that user. Internal audit logs still attribute impersonated actions to the original impersonator and store the target user details only in internal audit payload data.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateImpersonator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        impersonator: boolean;
    }): Promise<Models.User<Preferences>>;
    /**
     * Enable or disable whether a user can impersonate other users. When impersonation headers are used, the request runs as the target user for API behavior, while internal audit logs still attribute the action to the original impersonator and store the impersonated target details only in internal audit payload data.
     *
     *
     * @param {string} userId - User ID.
     * @param {boolean} impersonator - Whether the user can impersonate other users. When true, the user can browse project users to choose a target and can pass impersonation headers to act as that user. Internal audit logs still attribute impersonated actions to the original impersonator and store the target user details only in internal audit payload data.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateImpersonator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, impersonator: boolean): Promise<Models.User<Preferences>>;
    updateImpersonator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; impersonator: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; impersonator: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                impersonator: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                impersonator: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const impersonator = params.impersonator;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof impersonator === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "impersonator"',
            );
        }
        const apiPath = '/users/{userId}/impersonator'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof impersonator !== 'undefined') {
            apiPayload['impersonator'] = impersonator;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Use this endpoint to create a JSON Web Token for user by its unique ID. You can use the resulting JWT to authenticate on behalf of the user. The JWT secret will become invalid if the session it uses gets deleted.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.sessionId - Session ID. Use the string 'recent()' to use the most recent session, which is also the default.
     * @param {number} params.duration - Time in seconds before JWT expires. Default duration is 900 seconds, and maximum is 3600 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Jwt>}
     */
    createJWT(params: {
        userId: string;
        sessionId?: string;
        duration?: number;
    }): Promise<Models.Jwt>;
    /**
     * Use this endpoint to create a JSON Web Token for user by its unique ID. You can use the resulting JWT to authenticate on behalf of the user. The JWT secret will become invalid if the session it uses gets deleted.
     *
     * @param {string} userId - User ID.
     * @param {string} sessionId - Session ID. Use the string 'recent()' to use the most recent session, which is also the default.
     * @param {number} duration - Time in seconds before JWT expires. Default duration is 900 seconds, and maximum is 3600 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Jwt>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createJWT(
        userId: string,
        sessionId?: string,
        duration?: number,
    ): Promise<Models.Jwt>;
    createJWT(
        paramsOrFirst:
            { userId: string; sessionId?: string; duration?: number } | string,
        ...rest: [string?, number?]
    ): Promise<Models.Jwt> {
        let params: { userId: string; sessionId?: string; duration?: number };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                sessionId?: string;
                duration?: number;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                sessionId: rest[0] as string,
                duration: rest[1] as number,
            };
        }

        const userId = params.userId;
        const sessionId = params.sessionId;
        const duration = params.duration;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/jwts'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof sessionId !== 'undefined') {
            apiPayload['sessionId'] = sessionId;
        }
        if (typeof duration !== 'undefined') {
            apiPayload['duration'] = duration;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user labels by its unique ID.
     *
     * Labels can be used to grant access to resources. While teams are a way for user's to share access to a resource, labels can be defined by the developer to grant access without an invitation. See the [Permissions docs](https://appwrite.io/docs/permissions) for more info.
     *
     * @param {string} params.userId - User ID.
     * @param {string[]} params.labels - Array of user labels. Replaces the previous labels. Maximum of 1000 labels are allowed, each up to 36 alphanumeric characters long.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateLabels<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        labels: string[];
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user labels by its unique ID.
     *
     * Labels can be used to grant access to resources. While teams are a way for user's to share access to a resource, labels can be defined by the developer to grant access without an invitation. See the [Permissions docs](https://appwrite.io/docs/permissions) for more info.
     *
     * @param {string} userId - User ID.
     * @param {string[]} labels - Array of user labels. Replaces the previous labels. Maximum of 1000 labels are allowed, each up to 36 alphanumeric characters long.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateLabels<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, labels: string[]): Promise<Models.User<Preferences>>;
    updateLabels<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; labels: string[] } | string,
        ...rest: [string[]?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; labels: string[] };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                labels: string[];
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                labels: rest[0] as string[],
            };
        }

        const userId = params.userId;
        const labels = params.labels;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof labels === 'undefined') {
            throw new AppwriteException('Missing required parameter: "labels"');
        }
        const apiPath = '/users/{userId}/labels'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof labels !== 'undefined') {
            apiPayload['labels'] = labels;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('put', uri, apiHeaders, apiPayload);
    }

    /**
     * Get the user activity logs list by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.LogList>}
     */
    listLogs(params: {
        userId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.LogList>;
    /**
     * Get the user activity logs list by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Only supported methods are limit and offset
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.LogList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listLogs(
        userId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.LogList>;
    listLogs(
        paramsOrFirst:
            { userId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.LogList> {
        let params: { userId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const userId = params.userId;
        const queries = params.queries;
        const total = params.total;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/logs'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Get the user membership list by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, teamId, invited, joined, confirm, roles
     * @param {string} params.search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MembershipList>}
     */
    listMemberships(params: {
        userId: string;
        queries?: string[];
        search?: string;
        total?: boolean;
    }): Promise<Models.MembershipList>;
    /**
     * Get the user membership list by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, teamId, invited, joined, confirm, roles
     * @param {string} search - Search term to filter your list results. Max length: 256 chars.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MembershipList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listMemberships(
        userId: string,
        queries?: string[],
        search?: string,
        total?: boolean,
    ): Promise<Models.MembershipList>;
    listMemberships(
        paramsOrFirst:
            | {
                  userId: string;
                  queries?: string[];
                  search?: string;
                  total?: boolean;
              }
            | string,
        ...rest: [string[]?, string?, boolean?]
    ): Promise<Models.MembershipList> {
        let params: {
            userId: string;
            queries?: string[];
            search?: string;
            total?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                queries?: string[];
                search?: string;
                total?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                queries: rest[0] as string[],
                search: rest[1] as string,
                total: rest[2] as boolean,
            };
        }

        const userId = params.userId;
        const queries = params.queries;
        const search = params.search;
        const total = params.total;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/memberships'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof search !== 'undefined') {
            apiPayload['search'] = search;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Enable or disable MFA on a user account.
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.mfa - Enable or disable MFA.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Users.updateMFA` instead.
     */
    updateMfa<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        mfa: boolean;
    }): Promise<Models.User<Preferences>>;
    /**
     * Enable or disable MFA on a user account.
     *
     * @param {string} userId - User ID.
     * @param {boolean} mfa - Enable or disable MFA.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMfa<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, mfa: boolean): Promise<Models.User<Preferences>>;
    updateMfa<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; mfa: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; mfa: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string; mfa: boolean };
        } else {
            params = {
                userId: paramsOrFirst as string,
                mfa: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const mfa = params.mfa;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof mfa === 'undefined') {
            throw new AppwriteException('Missing required parameter: "mfa"');
        }
        const apiPath = '/users/{userId}/mfa'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof mfa !== 'undefined') {
            apiPayload['mfa'] = mfa;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Enable or disable MFA on a user account.
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.mfa - Enable or disable MFA.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateMFA<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        mfa: boolean;
    }): Promise<Models.User<Preferences>>;
    /**
     * Enable or disable MFA on a user account.
     *
     * @param {string} userId - User ID.
     * @param {boolean} mfa - Enable or disable MFA.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMFA<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, mfa: boolean): Promise<Models.User<Preferences>>;
    updateMFA<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; mfa: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; mfa: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string; mfa: boolean };
        } else {
            params = {
                userId: paramsOrFirst as string,
                mfa: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const mfa = params.mfa;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof mfa === 'undefined') {
            throw new AppwriteException('Missing required parameter: "mfa"');
        }
        const apiPath = '/users/{userId}/mfa'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof mfa !== 'undefined') {
            apiPayload['mfa'] = mfa;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete an authenticator app.
     *
     * @param {string} params.userId - User ID.
     * @param {AuthenticatorType} params.type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Users.deleteMFAAuthenticator` instead.
     */
    deleteMfaAuthenticator(params: {
        userId: string;
        type: AuthenticatorType;
    }): Promise<{}>;
    /**
     * Delete an authenticator app.
     *
     * @param {string} userId - User ID.
     * @param {AuthenticatorType} type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteMfaAuthenticator(
        userId: string,
        type: AuthenticatorType,
    ): Promise<{}>;
    deleteMfaAuthenticator(
        paramsOrFirst: { userId: string; type: AuthenticatorType } | string,
        ...rest: [AuthenticatorType?]
    ): Promise<{}> {
        let params: { userId: string; type: AuthenticatorType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                type: AuthenticatorType;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                type: rest[0] as AuthenticatorType,
            };
        }

        const userId = params.userId;
        const type = params.type;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/users/{userId}/mfa/authenticators/{type}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{type}', encodeURIComponent(String(type)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete an authenticator app.
     *
     * @param {string} params.userId - User ID.
     * @param {AuthenticatorType} params.type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteMFAAuthenticator(params: {
        userId: string;
        type: AuthenticatorType;
    }): Promise<{}>;
    /**
     * Delete an authenticator app.
     *
     * @param {string} userId - User ID.
     * @param {AuthenticatorType} type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteMFAAuthenticator(
        userId: string,
        type: AuthenticatorType,
    ): Promise<{}>;
    deleteMFAAuthenticator(
        paramsOrFirst: { userId: string; type: AuthenticatorType } | string,
        ...rest: [AuthenticatorType?]
    ): Promise<{}> {
        let params: { userId: string; type: AuthenticatorType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                type: AuthenticatorType;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                type: rest[0] as AuthenticatorType,
            };
        }

        const userId = params.userId;
        const type = params.type;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/users/{userId}/mfa/authenticators/{type}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{type}', encodeURIComponent(String(type)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a custom MFA challenge for a user, including the code to be delivered through your own channel.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.challengeId - ID of the challenge.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaChallengeSecret>}
     */
    getMFAChallenge(params: {
        userId: string;
        challengeId: string;
    }): Promise<Models.MfaChallengeSecret>;
    /**
     * Get a custom MFA challenge for a user, including the code to be delivered through your own channel.
     *
     * @param {string} userId - User ID.
     * @param {string} challengeId - ID of the challenge.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaChallengeSecret>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getMFAChallenge(
        userId: string,
        challengeId: string,
    ): Promise<Models.MfaChallengeSecret>;
    getMFAChallenge(
        paramsOrFirst: { userId: string; challengeId: string } | string,
        ...rest: [string?]
    ): Promise<Models.MfaChallengeSecret> {
        let params: { userId: string; challengeId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                challengeId: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                challengeId: rest[0] as string,
            };
        }

        const userId = params.userId;
        const challengeId = params.challengeId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof challengeId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "challengeId"',
            );
        }
        const apiPath = '/users/{userId}/mfa/challenges/{challengeId}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{challengeId}', encodeURIComponent(String(challengeId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * List the factors available on the account to be used as a MFA challange.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaFactors>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Users.listMFAFactors` instead.
     */
    listMfaFactors(params: { userId: string }): Promise<Models.MfaFactors>;
    /**
     * List the factors available on the account to be used as a MFA challange.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaFactors>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listMfaFactors(userId: string): Promise<Models.MfaFactors>;
    listMfaFactors(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaFactors> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/factors'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * List the factors available on the account to be used as a MFA challange.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaFactors>}
     */
    listMFAFactors(params: { userId: string }): Promise<Models.MfaFactors>;
    /**
     * List the factors available on the account to be used as a MFA challange.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaFactors>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listMFAFactors(userId: string): Promise<Models.MfaFactors>;
    listMFAFactors(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaFactors> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/factors'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Get recovery codes that can be used as backup for MFA flow by User ID. Before getting codes, they must be generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Users.getMFARecoveryCodes` instead.
     */
    getMfaRecoveryCodes(params: {
        userId: string;
    }): Promise<Models.MfaRecoveryCodes>;
    /**
     * Get recovery codes that can be used as backup for MFA flow by User ID. Before getting codes, they must be generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getMfaRecoveryCodes(userId: string): Promise<Models.MfaRecoveryCodes>;
    getMfaRecoveryCodes(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaRecoveryCodes> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/recovery-codes'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Get recovery codes that can be used as backup for MFA flow by User ID. Before getting codes, they must be generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     */
    getMFARecoveryCodes(params: {
        userId: string;
    }): Promise<Models.MfaRecoveryCodes>;
    /**
     * Get recovery codes that can be used as backup for MFA flow by User ID. Before getting codes, they must be generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getMFARecoveryCodes(userId: string): Promise<Models.MfaRecoveryCodes>;
    getMFARecoveryCodes(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaRecoveryCodes> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/recovery-codes'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Regenerate recovery codes that can be used as backup for MFA flow by User ID. Before regenerating codes, they must be first generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Users.updateMFARecoveryCodes` instead.
     */
    updateMfaRecoveryCodes(params: {
        userId: string;
    }): Promise<Models.MfaRecoveryCodes>;
    /**
     * Regenerate recovery codes that can be used as backup for MFA flow by User ID. Before regenerating codes, they must be first generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMfaRecoveryCodes(userId: string): Promise<Models.MfaRecoveryCodes>;
    updateMfaRecoveryCodes(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaRecoveryCodes> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/recovery-codes'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('put', uri, apiHeaders, apiPayload);
    }

    /**
     * Regenerate recovery codes that can be used as backup for MFA flow by User ID. Before regenerating codes, they must be first generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     */
    updateMFARecoveryCodes(params: {
        userId: string;
    }): Promise<Models.MfaRecoveryCodes>;
    /**
     * Regenerate recovery codes that can be used as backup for MFA flow by User ID. Before regenerating codes, they must be first generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMFARecoveryCodes(userId: string): Promise<Models.MfaRecoveryCodes>;
    updateMFARecoveryCodes(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaRecoveryCodes> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/recovery-codes'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('put', uri, apiHeaders, apiPayload);
    }

    /**
     * Generate recovery codes used as backup for MFA flow for User ID. Recovery codes can be used as a MFA verification type in [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method by client SDK.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Users.createMFARecoveryCodes` instead.
     */
    createMfaRecoveryCodes(params: {
        userId: string;
    }): Promise<Models.MfaRecoveryCodes>;
    /**
     * Generate recovery codes used as backup for MFA flow for User ID. Recovery codes can be used as a MFA verification type in [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method by client SDK.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMfaRecoveryCodes(userId: string): Promise<Models.MfaRecoveryCodes>;
    createMfaRecoveryCodes(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaRecoveryCodes> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/recovery-codes'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Generate recovery codes used as backup for MFA flow for User ID. Recovery codes can be used as a MFA verification type in [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method by client SDK.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     */
    createMFARecoveryCodes(params: {
        userId: string;
    }): Promise<Models.MfaRecoveryCodes>;
    /**
     * Generate recovery codes used as backup for MFA flow for User ID. Recovery codes can be used as a MFA verification type in [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method by client SDK.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMFARecoveryCodes(userId: string): Promise<Models.MfaRecoveryCodes>;
    createMFARecoveryCodes(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.MfaRecoveryCodes> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/mfa/recovery-codes'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user name by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateName<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        name: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user name by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateName<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, name: string): Promise<Models.User<Preferences>>;
    updateName<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; name: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; name: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string; name: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
                name: rest[0] as string,
            };
        }

        const userId = params.userId;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        const apiPath = '/users/{userId}/name'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user password by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.password - New user password. Must be at least 8 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updatePassword<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        password: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user password by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string} password - New user password. Must be at least 8 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePassword<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, password: string): Promise<Models.User<Preferences>>;
    updatePassword<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; password: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; password: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                password: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                password: rest[0] as string,
            };
        }

        const userId = params.userId;
        const password = params.password;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/users/{userId}/password'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user phone by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.number - User phone number.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updatePhone<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        number: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user phone by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string} number - User phone number.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePhone<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, number: string): Promise<Models.User<Preferences>>;
    updatePhone<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; number: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; number: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                number: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                number: rest[0] as string,
            };
        }

        const userId = params.userId;
        const number = params.number;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof number === 'undefined') {
            throw new AppwriteException('Missing required parameter: "number"');
        }
        const apiPath = '/users/{userId}/phone'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof number !== 'undefined') {
            apiPayload['number'] = number;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Get the user preferences by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Preferences>}
     */
    getPrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: { userId: string }): Promise<Preferences>;
    /**
     * Get the user preferences by its unique ID.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<Preferences>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string): Promise<Preferences>;
    getPrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(paramsOrFirst: { userId: string } | string): Promise<Preferences> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/prefs'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user preferences by its unique ID. The object you pass is stored as is, and replaces any previous value. The maximum allowed prefs size is 64kB and throws error if exceeded.
     *
     * @param {string} params.userId - User ID.
     * @param {object} params.prefs - Prefs key-value JSON object.
     * @throws {AppwriteException}
     * @returns {Promise<Preferences>}
     */
    updatePrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: { userId: string; prefs: object }): Promise<Preferences>;
    /**
     * Update the user preferences by its unique ID. The object you pass is stored as is, and replaces any previous value. The maximum allowed prefs size is 64kB and throws error if exceeded.
     *
     * @param {string} userId - User ID.
     * @param {object} prefs - Prefs key-value JSON object.
     * @throws {AppwriteException}
     * @returns {Promise<Preferences>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, prefs: object): Promise<Preferences>;
    updatePrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; prefs: object } | string,
        ...rest: [object?]
    ): Promise<Preferences> {
        let params: { userId: string; prefs: object };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string; prefs: object };
        } else {
            params = {
                userId: paramsOrFirst as string,
                prefs: rest[0] as object,
            };
        }

        const userId = params.userId;
        const prefs = params.prefs;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof prefs === 'undefined') {
            throw new AppwriteException('Missing required parameter: "prefs"');
        }
        const apiPath = '/users/{userId}/prefs'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof prefs !== 'undefined') {
            apiPayload['prefs'] = prefs;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Get the user sessions list by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SessionList>}
     */
    listSessions(params: {
        userId: string;
        total?: boolean;
    }): Promise<Models.SessionList>;
    /**
     * Get the user sessions list by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SessionList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listSessions(userId: string, total?: boolean): Promise<Models.SessionList>;
    listSessions(
        paramsOrFirst: { userId: string; total?: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.SessionList> {
        let params: { userId: string; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                total?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                total: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const total = params.total;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/sessions'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Creates a session for a user. Returns an immediately usable session object.
     *
     * If you want to generate a token for a custom authentication flow, use the [POST /users/{userId}/tokens](https://appwrite.io/docs/server/users#createToken) endpoint.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    createSession(params: { userId: string }): Promise<Models.Session>;
    /**
     * Creates a session for a user. Returns an immediately usable session object.
     *
     * If you want to generate a token for a custom authentication flow, use the [POST /users/{userId}/tokens](https://appwrite.io/docs/server/users#createToken) endpoint.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSession(userId: string): Promise<Models.Session>;
    createSession(
        paramsOrFirst: { userId: string } | string,
    ): Promise<Models.Session> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/sessions'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete all user's sessions by using the user's unique ID.
     *
     * @param {string} params.userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteSessions(params: { userId: string }): Promise<{}>;
    /**
     * Delete all user's sessions by using the user's unique ID.
     *
     * @param {string} userId - User ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteSessions(userId: string): Promise<{}>;
    deleteSessions(paramsOrFirst: { userId: string } | string): Promise<{}> {
        let params: { userId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
            };
        }

        const userId = params.userId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/sessions'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete a user sessions by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.sessionId - Session ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteSession(params: { userId: string; sessionId: string }): Promise<{}>;
    /**
     * Delete a user sessions by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {string} sessionId - Session ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteSession(userId: string, sessionId: string): Promise<{}>;
    deleteSession(
        paramsOrFirst: { userId: string; sessionId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { userId: string; sessionId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                sessionId: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                sessionId: rest[0] as string,
            };
        }

        const userId = params.userId;
        const sessionId = params.sessionId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof sessionId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "sessionId"',
            );
        }
        const apiPath = '/users/{userId}/sessions/{sessionId}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{sessionId}', encodeURIComponent(String(sessionId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user status by its unique ID. Use this endpoint as an alternative to deleting a user if you want to keep user's ID reserved.
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.status - User Status. To activate the user pass `true` and to block the user pass `false`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        status: boolean;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user status by its unique ID. Use this endpoint as an alternative to deleting a user if you want to keep user's ID reserved.
     *
     * @param {string} userId - User ID.
     * @param {boolean} status - User Status. To activate the user pass `true` and to block the user pass `false`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(userId: string, status: boolean): Promise<Models.User<Preferences>>;
    updateStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; status: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; status: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                status: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                status: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const status = params.status;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof status === 'undefined') {
            throw new AppwriteException('Missing required parameter: "status"');
        }
        const apiPath = '/users/{userId}/status'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof status !== 'undefined') {
            apiPayload['status'] = status;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * List the messaging targets that are associated with a user.
     *
     * @param {string} params.userId - User ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, providerId, identifier, providerType
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TargetList>}
     */
    listTargets(params: {
        userId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.TargetList>;
    /**
     * List the messaging targets that are associated with a user.
     *
     * @param {string} userId - User ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, providerId, identifier, providerType
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TargetList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listTargets(
        userId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.TargetList>;
    listTargets(
        paramsOrFirst:
            { userId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.TargetList> {
        let params: { userId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const userId = params.userId;
        const queries = params.queries;
        const total = params.total;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/targets'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Create a messaging target.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.targetId - Target ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {MessagingProviderType} params.providerType - The target provider type. Can be one of the following: `email`, `sms` or `push`.
     * @param {string} params.identifier - The target identifier (token, email, phone etc.)
     * @param {string} params.providerId - Provider ID. Message will be sent to this target from the specified provider ID. If no provider ID is set the first setup provider will be used.
     * @param {string} params.name - Target name. Max length: 128 chars. For example: My Awesome App Galaxy S23.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     */
    createTarget(params: {
        userId: string;
        targetId: string;
        providerType: MessagingProviderType;
        identifier: string;
        providerId?: string;
        name?: string;
    }): Promise<Models.Target>;
    /**
     * Create a messaging target.
     *
     * @param {string} userId - User ID.
     * @param {string} targetId - Target ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {MessagingProviderType} providerType - The target provider type. Can be one of the following: `email`, `sms` or `push`.
     * @param {string} identifier - The target identifier (token, email, phone etc.)
     * @param {string} providerId - Provider ID. Message will be sent to this target from the specified provider ID. If no provider ID is set the first setup provider will be used.
     * @param {string} name - Target name. Max length: 128 chars. For example: My Awesome App Galaxy S23.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createTarget(
        userId: string,
        targetId: string,
        providerType: MessagingProviderType,
        identifier: string,
        providerId?: string,
        name?: string,
    ): Promise<Models.Target>;
    createTarget(
        paramsOrFirst:
            | {
                  userId: string;
                  targetId: string;
                  providerType: MessagingProviderType;
                  identifier: string;
                  providerId?: string;
                  name?: string;
              }
            | string,
        ...rest: [string?, MessagingProviderType?, string?, string?, string?]
    ): Promise<Models.Target> {
        let params: {
            userId: string;
            targetId: string;
            providerType: MessagingProviderType;
            identifier: string;
            providerId?: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                targetId: string;
                providerType: MessagingProviderType;
                identifier: string;
                providerId?: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                targetId: rest[0] as string,
                providerType: rest[1] as MessagingProviderType,
                identifier: rest[2] as string,
                providerId: rest[3] as string,
                name: rest[4] as string,
            };
        }

        const userId = params.userId;
        const targetId = params.targetId;
        const providerType = params.providerType;
        const identifier = params.identifier;
        const providerId = params.providerId;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof targetId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        if (typeof providerType === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "providerType"',
            );
        }
        if (typeof identifier === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "identifier"',
            );
        }
        const apiPath = '/users/{userId}/targets'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof targetId !== 'undefined') {
            apiPayload['targetId'] = targetId;
        }
        if (typeof providerType !== 'undefined') {
            apiPayload['providerType'] = providerType;
        }
        if (typeof identifier !== 'undefined') {
            apiPayload['identifier'] = identifier;
        }
        if (typeof providerId !== 'undefined') {
            apiPayload['providerId'] = providerId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a user's push notification target by ID.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.targetId - Target ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     */
    getTarget(params: {
        userId: string;
        targetId: string;
    }): Promise<Models.Target>;
    /**
     * Get a user's push notification target by ID.
     *
     * @param {string} userId - User ID.
     * @param {string} targetId - Target ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getTarget(userId: string, targetId: string): Promise<Models.Target>;
    getTarget(
        paramsOrFirst: { userId: string; targetId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Target> {
        let params: { userId: string; targetId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                targetId: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                targetId: rest[0] as string,
            };
        }

        const userId = params.userId;
        const targetId = params.targetId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof targetId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        const apiPath = '/users/{userId}/targets/{targetId}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{targetId}', encodeURIComponent(String(targetId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Update a messaging target.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.targetId - Target ID.
     * @param {string} params.identifier - The target identifier (token, email, phone etc.)
     * @param {string} params.providerId - Provider ID. Message will be sent to this target from the specified provider ID. If no provider ID is set the first setup provider will be used.
     * @param {string} params.name - Target name. Max length: 128 chars. For example: My Awesome App Galaxy S23.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     */
    updateTarget(params: {
        userId: string;
        targetId: string;
        identifier?: string;
        providerId?: string;
        name?: string;
    }): Promise<Models.Target>;
    /**
     * Update a messaging target.
     *
     * @param {string} userId - User ID.
     * @param {string} targetId - Target ID.
     * @param {string} identifier - The target identifier (token, email, phone etc.)
     * @param {string} providerId - Provider ID. Message will be sent to this target from the specified provider ID. If no provider ID is set the first setup provider will be used.
     * @param {string} name - Target name. Max length: 128 chars. For example: My Awesome App Galaxy S23.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateTarget(
        userId: string,
        targetId: string,
        identifier?: string,
        providerId?: string,
        name?: string,
    ): Promise<Models.Target>;
    updateTarget(
        paramsOrFirst:
            | {
                  userId: string;
                  targetId: string;
                  identifier?: string;
                  providerId?: string;
                  name?: string;
              }
            | string,
        ...rest: [string?, string?, string?, string?]
    ): Promise<Models.Target> {
        let params: {
            userId: string;
            targetId: string;
            identifier?: string;
            providerId?: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                targetId: string;
                identifier?: string;
                providerId?: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                targetId: rest[0] as string,
                identifier: rest[1] as string,
                providerId: rest[2] as string,
                name: rest[3] as string,
            };
        }

        const userId = params.userId;
        const targetId = params.targetId;
        const identifier = params.identifier;
        const providerId = params.providerId;
        const name = params.name;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof targetId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        const apiPath = '/users/{userId}/targets/{targetId}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{targetId}', encodeURIComponent(String(targetId)));
        const apiPayload: Payload = {};
        if (typeof identifier !== 'undefined') {
            apiPayload['identifier'] = identifier;
        }
        if (typeof providerId !== 'undefined') {
            apiPayload['providerId'] = providerId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete a messaging target.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.targetId - Target ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteTarget(params: { userId: string; targetId: string }): Promise<{}>;
    /**
     * Delete a messaging target.
     *
     * @param {string} userId - User ID.
     * @param {string} targetId - Target ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteTarget(userId: string, targetId: string): Promise<{}>;
    deleteTarget(
        paramsOrFirst: { userId: string; targetId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { userId: string; targetId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                targetId: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                targetId: rest[0] as string,
            };
        }

        const userId = params.userId;
        const targetId = params.targetId;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof targetId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        const apiPath = '/users/{userId}/targets/{targetId}'
            .replace('{userId}', encodeURIComponent(String(userId)))
            .replace('{targetId}', encodeURIComponent(String(targetId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Returns a token with a secret key for creating a session. Use the user ID and secret and submit a request to the [PUT /account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process.
     *
     *
     * @param {string} params.userId - User ID.
     * @param {number} params.length - Token length in characters. The default length is 6 characters
     * @param {number} params.expire - Token expiration period in seconds. The default expiration is 15 minutes.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createToken(params: {
        userId: string;
        length?: number;
        expire?: number;
    }): Promise<Models.Token>;
    /**
     * Returns a token with a secret key for creating a session. Use the user ID and secret and submit a request to the [PUT /account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process.
     *
     *
     * @param {string} userId - User ID.
     * @param {number} length - Token length in characters. The default length is 6 characters
     * @param {number} expire - Token expiration period in seconds. The default expiration is 15 minutes.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createToken(
        userId: string,
        length?: number,
        expire?: number,
    ): Promise<Models.Token>;
    createToken(
        paramsOrFirst:
            { userId: string; length?: number; expire?: number } | string,
        ...rest: [number?, number?]
    ): Promise<Models.Token> {
        let params: { userId: string; length?: number; expire?: number };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                length?: number;
                expire?: number;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                length: rest[0] as number,
                expire: rest[1] as number,
            };
        }

        const userId = params.userId;
        const length = params.length;
        const expire = params.expire;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        const apiPath = '/users/{userId}/tokens'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof length !== 'undefined') {
            apiPayload['length'] = length;
        }
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('post', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user email verification status by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.emailVerification - User email verification status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateEmailVerification<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        emailVerification: boolean;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user email verification status by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {boolean} emailVerification - User email verification status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEmailVerification<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        emailVerification: boolean,
    ): Promise<Models.User<Preferences>>;
    updateEmailVerification<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; emailVerification: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; emailVerification: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                emailVerification: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                emailVerification: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const emailVerification = params.emailVerification;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof emailVerification === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "emailVerification"',
            );
        }
        const apiPath = '/users/{userId}/verification'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof emailVerification !== 'undefined') {
            apiPayload['emailVerification'] = emailVerification;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }

    /**
     * Update the user phone verification status by its unique ID.
     *
     * @param {string} params.userId - User ID.
     * @param {boolean} params.phoneVerification - User phone verification status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updatePhoneVerification<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        phoneVerification: boolean;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the user phone verification status by its unique ID.
     *
     * @param {string} userId - User ID.
     * @param {boolean} phoneVerification - User phone verification status.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePhoneVerification<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        userId: string,
        phoneVerification: boolean,
    ): Promise<Models.User<Preferences>>;
    updatePhoneVerification<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { userId: string; phoneVerification: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.User<Preferences>> {
        let params: { userId: string; phoneVerification: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                phoneVerification: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                phoneVerification: rest[0] as boolean,
            };
        }

        const userId = params.userId;
        const phoneVerification = params.phoneVerification;
        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof phoneVerification === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "phoneVerification"',
            );
        }
        const apiPath = '/users/{userId}/verification/phone'.replace(
            '{userId}',
            encodeURIComponent(String(userId)),
        );
        const apiPayload: Payload = {};
        if (typeof phoneVerification !== 'undefined') {
            apiPayload['phoneVerification'] = phoneVerification;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('patch', uri, apiHeaders, apiPayload);
    }
}
